<script setup lang="ts">
import { ref, provide, watchEffect, computed, type Ref, onMounted, nextTick, watch } from 'vue'

export interface UiDetailTabMeta {
  value: string
  label: string
  count?: number | Ref<number>
  disabled?: boolean
}

export interface UiDetailTabsProps {
  defaultValue?: string
  padContent?: boolean
}

const props = withDefaults(defineProps<UiDetailTabsProps>(), {
  padContent: true,
})

const model = defineModel<string>()

const items: Ref<UiDetailTabMeta[]> = ref([])
const tabListRef = ref<HTMLDivElement>()
const indicatorStyle = ref({ left: '0px', width: '0px' })

function registerItem(item: UiDetailTabMeta) {
  const existingIndex = items.value.findIndex((i) => i.value === item.value)
  if (existingIndex === -1) {
    items.value.push(item)
  } else {
    items.value[existingIndex] = item
  }
}

function unregisterItem(value: string) {
  items.value = items.value.filter((i) => i.value !== value)
}

provide('UiDetailTabs:registerItem', registerItem)
provide('UiDetailTabs:unregisterItem', unregisterItem)

const activeValue = computed(() => model.value)
provide('UiDetailTabs:activeValue', activeValue)

watchEffect(() => {
  if (!model.value && items.value.length > 0) {
    model.value = props.defaultValue || items.value.find(i => !i.disabled)?.value
  }
})

function getCount(item: UiDetailTabMeta): number | undefined {
  if (item.count === undefined) return undefined
  if (typeof item.count === 'number') return item.count
  return item.count.value
}

function updateIndicator() {
  if (!tabListRef.value || !model.value) return
  const activeBtn = tabListRef.value.querySelector<HTMLButtonElement>(`[data-tab-value="${model.value}"]`)
  if (activeBtn) {
    const listRect = tabListRef.value.getBoundingClientRect()
    const btnRect = activeBtn.getBoundingClientRect()
    indicatorStyle.value = {
      left: `${btnRect.left - listRect.left}px`,
      width: `${btnRect.width}px`,
    }
  }
}

watch(model, () => nextTick(updateIndicator))
watch(items, () => nextTick(updateIndicator), { deep: true })
onMounted(() => nextTick(updateIndicator))

function selectTab(value: string) {
  const item = items.value.find(i => i.value === value)
  if (item?.disabled) return
  model.value = value
}
</script>

<template>
  <div class="w-full">
    <!-- Tab bar -->
    <div class="relative" ref="tabListRef">
      <div class="flex gap-1 px-6 overflow-x-auto scrollbar-hide">
        <button
          v-for="item in items"
          :key="item.value"
          :data-tab-value="item.value"
          :disabled="item.disabled"
          @click="selectTab(item.value)"
          class="relative px-3 py-3.5 text-sm font-medium whitespace-nowrap transition-colors shrink-0
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1 rounded-sm"
          :class="[
            model === item.value
              ? 'text-gray-900'
              : item.disabled
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-400 hover:text-gray-600 cursor-pointer'
          ]"
        >
          {{ item.label }}
          <span
            v-if="getCount(item) !== undefined"
            class="ml-1.5 py-0.5 px-1.5 rounded-full text-[11px] font-medium"
            :class="model === item.value
              ? 'bg-gray-900/8 text-gray-700'
              : 'bg-gray-100 text-gray-400'"
          >
            {{ getCount(item) }}
          </span>
        </button>
      </div>

      <!-- Sliding indicator line -->
      <div class="absolute bottom-0 left-0 right-0 h-px bg-gray-100">
        <span
          class="absolute bottom-0 h-0.5 bg-gray-900 rounded-full transition-all duration-250 ease-out"
          :style="indicatorStyle"
        />
      </div>
    </div>

    <!-- Content -->
    <div :class="padContent ? 'p-6' : ''">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
